import {
  formatLocalDateTimeDateFns,
  internPath,
  pdfCloudPath,
} from "@/constants/constants";
import {
  MdBrokenImage,
  MdDirectionsBus,
  MdPeople,
  MdPublic,
  MdReportProblem,
  MdSettingsBackupRestore,
  MdSwapHoriz,
  MdTrendingFlat,
  MdEmail,
  MdPhone,
  MdPhoneAndroid,
  MdPrint,
  MdPerson,
  MdBusiness,
} from "react-icons/md";
import {
  RequestCompanyType,
  RequestType,
  TransferType,
} from "@/types/busrequest";
import { type GeoLocation } from "@/types/location";
import { env } from "@/env.mjs";
import { BaseReducerActions } from "@/types/reactHelperTypes";
import { CompanyInfoType } from "@/types/company";
import { DateFnsLocales, FullLocales, LocaleName } from "@/app/i18n/settings";
import { formatDate } from "date-fns";
import { Temporal } from "@js-temporal/polyfill";
import { FaBusAlt } from "react-icons/fa";
import {
  FaBuildingFlag,
  FaBuildingUser,
  FaPeopleRoof,
  FaSchool,
} from "react-icons/fa6";
import { IoMdPerson } from "react-icons/io";

/**
 * Function to get the data of current page of any table
 */
export function getTablePageData<A>(
  currentPage: number,
  itemsPerPage: number,
  data: A[],
): A[] {
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const pageData = data.slice(indexOfFirstItem, indexOfLastItem);

  return pageData;
}

// TODO: this regex does not work for whatever reason
// also saving this file removes escape characters which are needed in the regex
export function isEmail(email: string): boolean {
  return RegExp(
    /*"/^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/",*/
    "^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:.[a-zA-Z0-9-]+)*$",
  ).test(email);
}

export function getPdf(id: string): string {
  return pdfCloudPath + id;
}

export function getInternPdf(prefix: string): string {
  return internPath + prefix + ".pdf";
}

export function isElementOverflowing(element: HTMLElement | null): boolean {
  if (element === null) {
    return false;
  } else {
    const overflowX = element.offsetWidth < element.scrollWidth,
      overflowY = element.offsetHeight < element.scrollHeight;

    return overflowX || overflowY;
  }
}

export function getIconForRequestType(type: RequestType) {
  const SelectedIcon =
    type === "grouprequest"
      ? MdPeople
      : type === "vacancy"
        ? MdDirectionsBus
        : type === "emptyride"
          ? MdDirectionsBus
          : type === "emergency"
            ? MdReportProblem
            : MdBrokenImage;

  return (
    <div className="icon">
      <SelectedIcon />
    </div>
  );
}

export function getIconForRequestCompanyType(
  type: RequestCompanyType,
  isCompany?: boolean,
) {
  switch (type) {
    case "COMPANY":
      return isCompany ? <FaBusAlt /> : <FaBuildingUser />;
    case "SCHOOL":
      return <FaSchool />;
    case "CLUB":
      return <FaPeopleRoof />;
    case "PRIVATE":
      return <IoMdPerson />;
    case "AUTHORITY":
      return <FaBuildingFlag />;
    default:
      return <MdBrokenImage />;
  }
}

//TODO check icons
export function getIconForCompanyInfoType(
  type: CompanyInfoType | "contactPerson",
) {
  const SelectedIcon =
    type === "email"
      ? MdEmail
      : type === "telephone"
        ? MdPhone
        : type === "cellphone"
          ? MdPhoneAndroid
          : type === "fax"
            ? MdPrint
            : type === "website"
              ? MdPublic
              : type === "name"
                ? MdPerson
                : type === "contactPerson"
                  ? MdPerson
                  : MdBrokenImage;

  return (
    <div className="icon">
      <SelectedIcon />
    </div>
  );
}

export function getIconForTransferType(type: TransferType, size?: number) {
  const SelectedIcon =
    type === "transferoneway"
      ? MdTrendingFlat
      : type === "transfertwoway"
        ? MdSwapHoriz
        : type === "transfertour"
          ? MdSettingsBackupRestore
          : MdTrendingFlat;

  return (
    <div className="icon">
      <SelectedIcon size={size} />
    </div>
  );
}

// dist is in METERS!!!!!!
export function isNearToLocation(
  location: GeoLocation,
  nearTo: GeoLocation,
  dist: number,
): boolean {
  const dif = distance(
    location.latitude,
    location.longitude,
    nearTo.latitude,
    nearTo.longitude,
  );
  return dif < dist;
}

// Release utils

export function isStaging(): boolean {
  return !env.NEXT_PUBLIC_APP_VERSION?.includes("release");
}

export const isProduction =
  typeof window === "undefined"
    ? true
    : !window.location.href.includes("localhost");

export function sleep(ms: number): Promise<unknown> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function hsin(theta: number): number {
  return Math.pow(Math.sin(theta / 2), 2);
}

// Distance function returns the distance (in meters) between two points of
//     a given longitude and latitude relatively accurately (using a spherical
//     approximation of the Earth) through the Haversin Distance Formula for
//     great arc distance on a sphere with accuracy for small distances
//
// point coordinates are supplied in degrees and converted into rad. in the func
//
// distance returned is METERS!!!!!!
// http://en.wikipedia.org/wiki/Haversine_formula
export function distance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  // convert to radians
  // must cast radius as float to multiply later
  const la1 = (lat1 * Math.PI) / 180;
  const lo1 = (lon1 * Math.PI) / 180;
  const la2 = (lat2 * Math.PI) / 180;
  const lo2 = (lon2 * Math.PI) / 180;

  const r = 6378100; // Earth radius in METERS

  // calculate
  const h = hsin(la2 - la1) + Math.cos(la1) * Math.cos(la2) * hsin(lo2 - lo1);

  return 2 * r * Math.asin(Math.sqrt(h));
}

/**
 * This is a strongly typed forEach function for objects with many different field types.
 * It solves the problem of having the typing break when using Object.entries().
 *
 * Instead of having a key that is typed as "x" | "y" and a value that is typed as number | string,
 * we have a tuple {name: "x", payload: string} and {name: "y", payload: number}.
 * This way, we can use switch statements where the typing still works.
 *
 * @param obj The Object that should be iterated over.
 * @param f The function that should be called for each key-value pair. It takes a key-value pair in the form of
 * an object.
 */
export function objForEach<T>(
  obj: T,
  f: (tuple: BaseReducerActions<T>) => void,
): void {
  for (const k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k)) {
      f({ name: k, payload: obj[k] });
    }
  }
}

export function getGoogleMapsLinkWithAddress(address: string): string {
  return "https://maps.google.com/?q=" + address;
}

export function formatLocalDateTime(
  date: Date,
  lng: LocaleName,
  timeZone: string = "Europe/Berlin",
  formatOptions?: Omit<Intl.DateTimeFormatOptions, "calendar" | "timezone">,
) {
  const defaultFormatOptions: Intl.DateTimeFormatOptions = {
    dateStyle: "medium",
    timeStyle: "short",
  };

  return Temporal.Instant.fromEpochMilliseconds(date.getTime())
    .toZonedDateTimeISO(timeZone)
    .toLocaleString(FullLocales[lng], {
      ...defaultFormatOptions,
      ...formatOptions,
    });
}

// Expects seconds since epoch in UTC time. Converts the UTC timezone into Europe/Berlin timezone by default.
export function formatStripeDate(
  dateSeconds: number,
  lng: LocaleName,
  timeZone: string = "Europe/Berlin",
  formatOptions?: Omit<Intl.DateTimeFormatOptions, "calendar" | "timezone">,
) {
  const defaultFormatOptions: Intl.DateTimeFormatOptions = {
    dateStyle: "medium",
    timeStyle: "short",
  };
  const temporalDate = new Temporal.ZonedDateTime(
    BigInt(dateSeconds) * BigInt(1000000000),
    "UTC", // Stripe seconds are always in UTC time
    "gregory",
  ).withTimeZone(timeZone);

  const germandSuffix = lng === "de" ? " Uhr" : "";

  return (
    temporalDate.toLocaleString(FullLocales[lng], {
      ...defaultFormatOptions,
      ...formatOptions,
    }) + germandSuffix
  );
}

/**
 * Escapes a string to be used in a regex pattern.
 * This is useful for creating dynamic regex patterns where special characters need to be escaped.
 * @param pattern The string to escape.
 * @returns The escaped string.
 */
export function escapePattern(pattern: string): string {
  return pattern.replace(/[.*#+?^${}()|[\]\\]/g, "\\$&").trim();
}
